import React, {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {Box, Button, Chip, Grid, Typography} from '@mui/material';
import {Container} from '@mui/system';
import POCToolMiniTool from '../../../POCWebPortal/POCToolSinglePage/POCToolSinglePageContent/POCToolMiniTool';
import POCToolCategory from '../../../POCWebPortal/POCToolSinglePage/POCToolCategory';
import Loader from '../../../POCWebPortal/generalPurpose/Loader';
import {useNavigate, useParams} from 'react-router-dom';
import {actionCreators} from '../../../../store/poctoolsContentManagement/poctoolsContentManagementActions';
import {editPOCTool} from '../helper/editPOCTool';
import {publishPOCTools} from '../../../../api/POCCMSPortalAPI/pointOfCareTools/POCToolsCalls';
import HeaderBar from '../helper/POCToolCreatorHeaderBar';
import Switcher from './Switcher';
import SearchBar from '../../../POCWebPortal/POCToolSinglePage/SearchBar';
import moment from 'moment';

interface Props {
  goBackStage: Function;
}

export default function PreviewPage(props: Props) {
  const {goBackStage} = props;
  let {id} = useParams();
  id = id?.slice(1);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsPublished] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isMobileMode, setIsMobileMode] = useState(false);

  const pocToolSummary = useAppSelector(
    state => state.poctoolsContentManagementState.pointOfCareTool,
  );
  const displayPocToolContent = useAppSelector(
    state => state.poctoolsContentManagementState.displayPocToolContent,
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleEditExistingPOCTool = async isPublished => {
    editPOCTool({
      pocToolSummary,
      displayPocToolContent,
      id,
      setIsLoading,
      setIsSubmitted: isPublished ? setIsPublished : setIsSubmitted,
      navigate,
    }).then(() => {
      if (isPublished) {
        publishPOCTools(id);
      }
      dispatch(actionCreators.ClearPOCTool());
      setTimeout(() => {
        navigate('/POCtoolsCMSHome');
      }, 3000);
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Loader />
      </Container>
    );
  }
  if (isSaved) {
    return (
      <Container style={styles.savedContainer} maxWidth="md">
        <Typography variant="h4" align="center">
          Your Point-of-Care Tool is published!
        </Typography>
      </Container>
    );
  }
  if (isSubmitted) {
    return (
      <Container style={styles.savedContainer} maxWidth="md">
        <Typography variant="h4" align="center">
          Your Point-of-Care Tool is saved!
        </Typography>
      </Container>
    );
  }

  return (
    <Box style={styles.container}>
      <Box paddingBottom={2}>
        <HeaderBar
          title={'Preview'}
          RightComponent={() => Switcher({isMobileMode, setIsMobileMode})}
        />
      </Box>
      <Box justifyContent={'center'} alignItems={'center'} display={'flex'}>
        <Box
          width={isMobileMode ? 415 : '100%'}
          height={isMobileMode ? 700 : '100%'}
          overflow={isMobileMode ? 'auto' : 'hidden'}
          padding={2}
          border={isMobileMode ? 2 : 0}
          borderRadius={2}
          borderColor={'#C3C3C3'}>
          {isMobileMode && (
            <>
              <Chip
                label="Point-of-Care Tool"
                style={{borderRadius: 4, fontSize: 10, color: '#545454'}}
                size="small"
              />
              <Typography style={styles.titleStyle}>
                {pocToolSummary.name}
              </Typography>
              <Typography style={styles.updatedDateStyle}>
                Updated on{' '}
                {moment(pocToolSummary.lastUpdated)
                  .format('MM/DD/YYYY')
                  .toString()}
              </Typography>
              <Box paddingBottom={2}>
                <SearchBar />
              </Box>
            </>
          )}

          {pocToolSummary.miniTools.length > 0
            ? //if it's minitools
              pocToolSummary.miniTools
                ?.slice()
                .sort((a, b) => a.order - b.order)
                .map(minitool => (
                  <div key={minitool.id} style={styles.minitoolContainer}>
                    <div style={styles.minitool}>
                      <POCToolMiniTool
                        miniToolsId={minitool.id}
                        poctoolsId={pocToolSummary.id}
                        title={minitool.name}
                      />
                    </div>
                    {minitool.categories
                      ?.slice()
                      .sort((a, b) => a.order - b.order)
                      .map(category => (
                        <POCToolCategory
                          key={category.id}
                          category={category}
                          isOpen={true}
                        />
                      ))}
                  </div>
                ))
            : pocToolSummary.categories
                ?.slice()
                .sort((a, b) => a.order - b.order)
                .map(category => (
                  <POCToolCategory
                    key={category.id}
                    category={category}
                    isOpen={true}
                  />
                ))}
        </Box>
      </Box>
      <Grid style={styles.continueButton}>
        <Button
          variant="contained"
          style={styles.saveButton}
          onClick={() => goBackStage()}>
          Back to Content Builder
        </Button>
        <Box>
          <Button
            onClick={() => handleEditExistingPOCTool(false)}
            variant="contained"
            style={styles.saveButton}>
            Save
          </Button>
          <Button
            onClick={() => handleEditExistingPOCTool(true)}
            variant="contained">
            Publish
          </Button>
        </Box>
      </Grid>
    </Box>
  );
}

const styles = {
  container: {
    width: '100%',
    display: 'flex',
    paddingTop: 30,
    flexDirection: 'column',
  },
  continueButton: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 50,
    paddingTop: 50,
  },
  saveButton: {marginRight: 20},
  POCToolSummaryItemContainer: {
    border: '1px solid #DDDDDD',
    padding: 10,
    borderRadius: 10,
    maxWidth: 400,
    marginBottom: 20,
  },
  minitoolContainer: {
    backgroundColor: '#ECDAEC',
    padding: 20,
    marginBottom: 5,
  },
  minitool: {pointerEvents: 'none'},
  savedContainer: {marginTop: '25%'},
  titleStyle: {
    fontSize: 27,
    fontWeight: '600',
    color: '#2569AF',
  },
  updatedDateStyle: {color: '#333333', fontSize: 14, paddingBottom: 16},
};
